import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { LoginPage } from './pages/LoginPage'
import { ChatPage } from './pages/ChatPage'
import { SearchPage } from './pages/SearchPage'
import { ManagementPage } from './pages/ManagementPage'
import { AnalyticsPage } from './pages/AnalyticsPage'
import KpiDashboard from './pages/KpiDashboard'
import { AuthCallback } from './pages/AuthCallback'
import { AnimatedBackground } from './components/AnimatedBackground'
import { OfflineIndicator } from './components/OfflineIndicator'
import { ConnectionRecovery } from './components/ConnectionRecovery'
import { useServiceWorker } from './hooks/useServiceWorker'
import { ErrorBoundary, setupGlobalErrorHandlers } from './components/ErrorBoundary'
import { MobileErrorLogger } from './components/debug/MobileErrorLogger'
import { useEffect } from 'react'
import './utils/clear-cache' // Import cache clearing utility

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false
        }
        return failureCount < 3
      },
      onError: (error) => {
        console.error('Query error:', error)
      },
    },
    mutations: {
      onError: (error) => {
        console.error('Mutation error:', error)
      },
    },
  },
})

function App() {
  // Register service worker for PWA support
  useServiceWorker()

  // Setup global error handlers
  useEffect(() => {
    setupGlobalErrorHandlers()
  }, [])

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <div className="min-h-screen relative">
              <AnimatedBackground />
              {/* <OfflineIndicator /> - Temporarily disabled due to false positives */}
              {/* <ConnectionRecovery /> - Temporarily disabled due to false positives */}
              <MobileErrorLogger />
              <Routes>
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <ChatPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/search"
                element={
                  <ProtectedRoute>
                    <SearchPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/kpi"
                element={
                  <ProtectedRoute>
                    <KpiDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/management"
                element={
                  <ProtectedRoute requiredRole="management">
                    <ManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute requiredRole="management">
                    <AnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster
              position="top-right"
              toastOptions={{
                className: 'glass text-white',
                style: {
                  background: 'rgba(0, 0, 0, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App